import {
  Modal,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import React, { FC, useMemo, useState } from 'react';
import { STYLE_VARS } from '../../../constants/style-vars';
import { Image } from '../../../components/Image';
import { useIsDesktop } from '../../../hooks/useIsDeskTop';
import { IS_IOS, IS_WEB } from '../../../constants/platform';
import { addBase64Prefix } from '../../../services/addBase64Prefix';
import { close_icon } from '../../../assets/base64';
import { DropDownItem } from '../../../models/DropDownItem';
import { FONTS } from '../../../constants';

interface SportDropDownProps {
  placeholder?: string;
  title?: string;
  label?: string;
  items: DropDownItem[];
  horizontal?: boolean;
  onChange?: (value: DropDownItem) => void;
}

const SportDropDown: FC<SportDropDownProps> = ({
  items,
  placeholder = 'SELECT',
  title,
  horizontal = false,
  onChange,
  label,
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const [value, setValue] = useState<DropDownItem>();
  const isDesktop = useIsDesktop();

  const renderDropDown = useMemo(() => {
    return (
      <TouchableOpacity
        style={[styles.dropdown, {}]}
        onPress={() => {
          setOpen(!open);
        }}
      >
        <View>
          <Text style={styles.textStyled}>{value?.label || placeholder}</Text>
        </View>
        <Image
          source={
            open == true
              ? require('../../../assets/up-arrow.png')
              : require('../../../assets/down-arrow.png')
          }
          style={{
            width: 15,
            height: 8,
            tintColor: STYLE_VARS.fccDarkGrey,
          }}
        />
      </TouchableOpacity>
    );
  }, [open, placeholder, value]);

  const renderPicker = useMemo(() => {
    if (isDesktop()) {
      if (open) {
        return (
          <View
            style={[
              styles.dropDownContainer,
              horizontal && { width: '55%', right: 0, top: 40 },
            ]}
          >
            <ScrollView contentContainerStyle={{ flexGrow: 1 }}>
              {items.map((item) => (
                <TouchableOpacity
                  style={{
                    paddingHorizontal: 20,
                    paddingVertical: 10,
                  }}
                  key={item.label}
                  onPress={() => {
                    setValue(item);
                    setOpen(false);
                    if (onChange) {
                      onChange(item);
                    }
                  }}
                >
                  <Text style={styles.textStyled}>{item.label}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        );
      }
    } else {
      return (
        <Modal animationType="slide" visible={open}>
          <View
            style={[
              {
                paddingVertical: 20,
                paddingHorizontal: 20,
                marginBottom: 20,
                borderBottomColor: STYLE_VARS.fccDarkGrey,
                borderBottomWidth: 1,
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
              },
              IS_IOS && { paddingTop: 60 },
            ]}
          >
            <Text style={[styles.textStyled, { fontSize: 20 }]}>{title}</Text>
            <TouchableOpacity
              onPress={() => {
                setOpen(false);
              }}
            >
              <Image
                source={
                  IS_WEB
                    ? addBase64Prefix(close_icon)
                    : require('../../../assets/close-icon.png')
                }
                style={{
                  width: 20,
                  height: 20,
                  tintColor: STYLE_VARS.fccDarkerGrey,
                }}
              />
            </TouchableOpacity>
          </View>
          <ScrollView
            contentContainerStyle={{ flexGrow: 1, paddingHorizontal: 20 }}
          >
            {items.map((item) => (
              <TouchableOpacity
                style={{
                  paddingVertical: 20,
                  borderBottomColor: STYLE_VARS.fccDarkGrey,
                  borderBottomWidth: 0.5,
                }}
                key={item.label}
                onPress={() => {
                  setValue(item);
                  setOpen(false);
                  if (onChange) {
                    onChange(item);
                  }
                }}
              >
                <Text style={styles.textStyled}>{item.label}</Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </Modal>
      );
    }
  }, [open, items, title, onChange, isDesktop, horizontal]);

  const renderComponent = useMemo(() => {
    if (horizontal) {
      return (
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            zIndex: 1,
          }}
        >
          <View style={{ width: '40%', zIndex: 1 }}>
            <Text style={styles.headerDropdownText}>{label}</Text>
          </View>
          <View style={{ width: '55%', zIndex: 1 }}>{renderDropDown}</View>
        </View>
      );
    }
    return (
      <View style={{ zIndex: 1 }}>
        <View style={{ marginBottom: 10 }}>
          <Text style={styles.headerDropdownText}>{label}</Text>
        </View>
        <View>{renderDropDown}</View>
      </View>
    );
  }, [renderDropDown, horizontal, label]);

  return (
    <View style={{ marginBottom: 20 }}>
      {renderComponent}
      {renderPicker}
    </View>
  );
};

export default SportDropDown;

const styles = StyleSheet.create({
  dropdown: {
    backgroundColor: 'white',
    flexDirection: 'row',
    borderColor: STYLE_VARS.fccDarkGrey,
    borderWidth: 0.5,
    borderRadius: 0,
    height: 40,
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
  },
  headerDropdownText: {
    color: STYLE_VARS.textColor,
    textTransform: 'uppercase',
    fontFamily: FONTS.IowanOldStyleBTRoman,
    fontSize: 13,
    fontWeight: '500',
  },
  textStyled: {
    color: STYLE_VARS.fccDarkerGrey,
    textTransform: 'uppercase',
    fontFamily: FONTS.IowanOldStyleBTRoman,
    fontSize: 12,
    fontWeight: '500',
  },
  dropDownContainer: {
    flex: 1,
    position: 'absolute',
    top: 66,
    maxHeight: 150,
    backgroundColor: 'white',
    minHeight: 50,
    zIndex: 1000,
    width: '100%',
    borderWidth: 1,
    borderColor: 'white',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
});
